import type { RouteParams } from 'vue-router';
import type { ResourceEntitlement, SimpleEntitlement } from '#layers/access/utils/entitlements';
import { usePermissionsStore } from '#layers/access/stores/permissions';
import { getKeyId } from '#layers/access/utils/get-key-id';

/** A `can*` entitlement constant value: a bare `resource:action` or a scoped `(id) => ...` result. */
export type CanEntitlement = SimpleEntitlement | ResourceEntitlement;

/** A bound entitlement check — the `can` of `useEntitlement()`. */
export type EntitlementChecker = (entitlement: CanEntitlement) => boolean;

/**
 * The options bag a route's `isVisible` gate receives. An object (not a bare `can`) so it can grow
 * more context later without re-signing every predicate. Predicates compose freely: `({ can }) => can(a) || can(b)`.
 *
 * `params` are present only on the deep-link middleware path (the gated route's resolved params),
 * so a detail gate can be per-id there. Nav consumers that evaluate `isVisible` over route
 * definitions (no concrete params) omit it, leaving a scoped gate as an any-id check — the correct
 * decision for whether to show the nav entry.
 */
export interface VisibilityContext {
	can: EntitlementChecker;
	params?: RouteParams;
	/**
	 * Whether the current user is an org admin (mirrors the engine's org-entitlement check).
	 * Org-level pages — editing/deleting the workspace, org settings — gate on this rather than a
	 * workspace entitlement, since `workspace:update`/`delete` are org-admin-gated, not policy-grantable.
	 */
	isOrganizationAdmin?: boolean;
}

/**
 * Direct entitlement gate for non-CRUD actions (assign a role/policy, add/remove a member).
 * Unlike `authorize` there is no item-rule fallback — these actions are purely entitlement-gated.
 * A scoped entitlement keeps its `resourceId` (normalized to the wire string, so a specific id is
 * an exact check and a missing id is an any-id check); a simple entitlement stays a bare
 * `resource:action` exact check. Admin bypasses via the store's `hasEntitlement`.
 */
export function useEntitlement() {
	const permissions = usePermissionsStore();

	function can(entitlement: CanEntitlement): boolean {
		if ('resourceId' in entitlement) {
			return permissions.hasEntitlement({
				resource: entitlement.resource,
				action: entitlement.action,
				resourceId: getKeyId(entitlement.resourceId),
			});
		}

		return permissions.hasEntitlement(entitlement);
	}

	/**
	 * The ids the user may act on for an entitlement's `resource:action` (the specific id, if any,
	 * is ignored), or `'*'` for all. Use to scope a picker to the assignable items.
	 */
	function allowedIds(entitlement: CanEntitlement): string[] | '*' {
		return permissions.allowedResourceIds(entitlement.resource, entitlement.action);
	}

	return { can, allowedIds };
}
